import {BASE_SYSTEM_PROMPT} from "./base-system-prompt-web-modeler.js";

const TEXT_ENCODER = new TextEncoder();
const MAX_CHARACTERS_OPEN_AI = 4000000;
export default class Copilot {
    /**
     * @param {import('@camunda/feel-copilot-client').CopilotConfiguration} configuration
     */
    constructor(configuration) {
        const {
            invoke
        } = configuration;

        /** @type {import('@camunda/feel-copilot-client').InvokeCallback} */
        this._invoke = invoke;

        /** @type {import('@camunda/feel-copilot-client').InternalMessage[]} */
        this._messages = [];
    }

    /**
     * Get messages.
     *
     * @returns {import('@camunda/feel-copilot-client').InternalMessage[]} messages
     */
    getMessages() {
        return this._messages;
    }

    /**
     * Invoke the FEEL Copilot.
     *
     * @param {import('@camunda/feel-copilot-client').InvokeRequest} request
     *
     * @returns {import('@camunda/feel-copilot-client').InvokeResponse}
     */
    invoke(request) {
        try {
            const controller = new AbortController();
            const systemPrompt = BASE_SYSTEM_PROMPT;

            const unfilteredVariables = request.variablesFromDiagram;

            const filteredVariables = (unfilteredVariables && unfilteredVariables.length > 0) ?
                unfilteredVariables.map(this._getRelevantVariableContent).filter((variable) => variable) : [];

            const newMessage = {
                role: 'user',
                text: request.prompt,
                feel: request.expression,
                context: request.context,
                variables: filteredVariables
            }

            const requestCharacters = TEXT_ENCODER.encode(JSON.stringify(newMessage)).length
            if (requestCharacters > MAX_CHARACTERS_OPEN_AI) {
                return {
                    response: Promise.resolve({status: 'inputTooLong'}),
                    cancel: () => {
                    }
                }
            }



            const newMessages = [
                newMessage
            ];

            const messagesToSend = [{
                role: 'system',
                text: systemPrompt
            }, ...this._messages.slice(-20), ...newMessages];

            const invokeMessages = Copilot.#toInvokeMessages(messagesToSend);

            const response = this._invoke({
                input: {messages: invokeMessages, model: 'gpt-4.1'},
                signal: controller.signal
            })
                .then((response) => this._handleInvokeSuccess(response, newMessages))
                .catch(this._handleInvokeFailure);

            return {
                response,
                cancel: () => controller.abort()
            }
        } catch (e) {
            return {
                response: Promise.resolve({status: 'otherError', message: e.message}),
                cancel: () => {}
            }
        }
    }

    async _handleInvokeSuccess(response, newMessages) {
        // request valid, add user message
        this._messages.push(...newMessages);

        const responseText = response?.choices?.[0]?.message?.content;
        if (!responseText) {
            return {status: "invalidCopilotResponse"};
        }
        const text = this._getTagContent(responseText, "markdown_free_text");
        const feel = this._getTagContent(responseText, "detailed_solution");
        const contextString = this._getTagContent(responseText, "context");

        let context;
        try {
            context = JSON.parse(contextString);
        } catch (e) {
            context = null;
        }


        this._messages.push({
            role: 'assistant',
            text,
            feel,
            context
        })

        return {status: "success", text, feel, context}
    };

    _handleInvokeFailure(reason) {
        if (reason instanceof Error && reason.name === 'AbortError') {
            return {status: 'cancelled'}
        }

        return {status: 'invokeError', message: `Something went wrong on our end. Please try again. Reason: ${reason?.message}`}
    }
    
    _getRelevantVariableContent(variable) {
        return !variable ? null : {
            name: variable.name,
            origin_type: variable.origin?.[0]?.$type,
            origin_id: variable.origin?.[0]?.id,
            scope: variable.scope?.$type,
            scope_name: variable.scope?.name
        }
    }

    _getTagContent(text, tag) {
        const regex = new RegExp(`<${ tag }>(.*?)</${ tag }>`, 's');

        const match = !text ? '' : text.match(regex);

        const contentWithCData = match ? match[1].trim() : null;
        return this.#trimCData(contentWithCData);
    }

    #trimCData(text) {
        const cdataPattern = /<!\[CDATA\[((.|\s)*)]]>/;
        const match = text?.match(cdataPattern);
        const result = match ? match[1] : text;
        return result?.trim();
    }

    static #toInvokeMessages(messages) {
        let totalMessageSize = 0;
        let maxMessageSizeReached = false;
        return messages.toReversed().map((message) => {
            if (maxMessageSizeReached) {
                return null;
            }
            const invokeMessage = Copilot.#toInvokeMessage(message);
            const messageSize = TEXT_ENCODER.encode(JSON.stringify(invokeMessage)).length;
            totalMessageSize += messageSize

            if (totalMessageSize <= MAX_CHARACTERS_OPEN_AI) {
                return invokeMessage;
            } else {
                maxMessageSizeReached = true;
                return null;
            }
        }).filter((message) => message !== null).toReversed();
    };

    static #toInvokeMessage(message) {
        if (message.role === 'user') {
            return {
                role: message.role,
                content: `<user_data>
                   <variables>
                      ${JSON.stringify(message.variables)}
                   </variables>
                   <input_context>
                      ${JSON.stringify(message.context)}
                   </input_context>
                   <input_expression>${JSON.stringify(message.feel)}</input_expression>
                   <input_text>${JSON.stringify(message.text)}</input_text>
                </user_data>`
            }
        } else {
            return {
                role: message.role,
                content: message.text
            }
        }
    }

}